import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn } from '../lib/supabase';
import { CaeraLogo } from '../components/CaeraLogo';

const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 15 * 60 * 1000; // 15 minutes

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  // Load lockout state from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem('login_locked_until');
    if (stored) {
      const until = parseInt(stored);
      if (until > Date.now()) setLockedUntil(until);
      else sessionStorage.removeItem('login_locked_until');
    }
    const storedAttempts = sessionStorage.getItem('login_attempts');
    if (storedAttempts) setAttempts(parseInt(storedAttempts));
  }, []);

  // Countdown timer for lockout
  useEffect(() => {
    if (!lockedUntil) return;
    const interval = setInterval(() => {
      const remaining = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockedUntil(null);
        setAttempts(0);
        setCountdown(0);
        sessionStorage.removeItem('login_locked_until');
        sessionStorage.removeItem('login_attempts');
        clearInterval(interval);
      } else {
        setCountdown(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (lockedUntil && lockedUntil > Date.now()) return;
    setLoading(true);
    setError('');

    const { error: err } = await signIn(email, password);

    if (err) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      sessionStorage.setItem('login_attempts', newAttempts);

      if (newAttempts >= MAX_ATTEMPTS) {
        const lockUntil = Date.now() + LOCKOUT_MS;
        setLockedUntil(lockUntil);
        sessionStorage.setItem('login_locked_until', lockUntil);
        setError(`Too many failed attempts. Account locked for 15 minutes.`);
      } else {
        setError(`Invalid email or password. ${MAX_ATTEMPTS - newAttempts} attempt${MAX_ATTEMPTS - newAttempts === 1 ? '' : 's'} remaining.`);
      }
      setLoading(false);
      return;
    }

    // Success — clear attempts
    sessionStorage.removeItem('login_attempts');
    sessionStorage.removeItem('login_locked_until');
    navigate('/dashboard');
  };

  const isLocked = lockedUntil && lockedUntil > Date.now();
  const formatCountdown = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  return (
    <div style={{ minHeight:'100vh', background:'var(--caera-navy)', display:'flex', alignItems:'center', justifyContent:'center', padding:20, fontFamily:'DM Sans, sans-serif' }}>
      <div style={{ width:'100%', maxWidth:420 }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:16 }}>
            <CaeraLogo size={48} darkBg textRatio={1.0} />
          </div>
          <div style={{ color:'var(--caera-light-copper)', fontSize:11, fontWeight:400, fontFamily:'var(--font-display)', letterSpacing:'0.16em', textTransform:'uppercase', marginBottom:22 }}>Care Management</div>
          <img src="/ahfc-logo.png" alt="A Heart For Care" style={{ height:88, marginBottom:22 }} />
          <p style={{ color:'rgba(255,255,255,0.45)', fontSize:13 }}>Staff portal — sign in to continue</p>
        </div>
        <div style={{ background:'#fff', borderRadius:12, padding:32 }}>
          {isLocked ? (
            <div style={{ textAlign:'center', padding:20 }}>
              <div style={{ fontSize:32, marginBottom:12 }}>🔒</div>
              <div style={{ fontSize:15, fontWeight:600, color:'var(--danger)', marginBottom:8 }}>Account temporarily locked</div>
              <div style={{ fontSize:13, color:'var(--text3)', marginBottom:8 }}>Too many failed login attempts.</div>
              <div style={{ fontSize:24, fontWeight:600, color:'var(--accent)', fontFamily:'DM Mono, monospace' }}>{formatCountdown(countdown)}</div>
              <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>remaining</div>
            </div>
          ) : (
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label className="form-label">Email address</label>
                <input
                  type="email" className="form-input" value={email}
                  onChange={e => setEmail(e.target.value.trim())}
                  placeholder="you@aheartforcare.com" required autoFocus
                  autoComplete="email"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password" className="form-input" value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required
                  autoComplete="current-password"
                />
              </div>
              {error && (
                <div style={{ background:'var(--danger-bg)', border:'1px solid var(--danger-border)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'var(--danger)' }}>{error}</div>
              )}
              {attempts > 0 && attempts < MAX_ATTEMPTS && (
                <div style={{ background:'var(--warn-bg)', border:'1px solid var(--warn-border)', borderRadius:8, padding:'8px 12px', marginBottom:16, fontSize:12, color:'var(--warn)' }}>
                  Warning: {MAX_ATTEMPTS - attempts} login attempt{MAX_ATTEMPTS - attempts === 1 ? '' : 's'} remaining before lockout.
                </div>
              )}
              <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
                {loading ? <span className="loading-spinner" style={{ borderTopColor:'#fff' }} /> : null}
                Sign in
              </button>
            </form>
          )}
          <div style={{ marginTop:16, textAlign:'center', fontSize:12, color:'var(--text3)' }}>
            Contact your administrator to reset your password.
          </div>
        </div>
        <div style={{ textAlign:'center', marginTop:24 }}>
          <a href="/intake" style={{ color:'rgba(255,255,255,0.4)', fontSize:12, textDecoration:'none' }}>Client intake form →</a>
        </div>
      </div>
    </div>
  );
}
